import { Injectable } from '@angular/core';
import { SimpleBatchData, SimpleBatchesDialogData, SimpleLabelData } from './simple-batches-viewer.component';
import { WorkProgressMaterialItem } from 'app/modules/components/monitor/monitor-routes/interfaces/work-progress-material.interface';

// Interfaces for input data structures
export interface MaterialInfo {
  code?: string;
  name?: string;
  _id?: string;
}

export interface BatchInput {
  bacth?: string;
  batch?: string;
  quantity?: number;
  parallelQuantity?: number;
  labels?: LabelInput[];
}

export interface LabelInput {
  label?: string;
  quantity?: number;
  parallelQuantity?: number;
}

export interface OrderDetailInput {
  material?: MaterialInfo;
  batches?: BatchInput[];
  deliveredBatches?: BatchInput[];
  pararellQuantity?: number;
  baseQuantity?: number;
}

export interface OrderInput {
  serial?: string;
  pararellQuantity?: number;
  baseQuantity?: number;
}

export interface WorkProgressMaterialInput {
  materialCode: string;
  materialName: string;
  batches: BatchInput[];
  baseQuantity: number;
  parallelQuantity: number;
  baseUnitOfMeasure: string;
  parallelUnitOfMeasure: string;
  originalDocument: OrderInput;
}

export interface SimpleBatchesOptions {
  title?: string;
  showUMP?: boolean;
  showLabels?: boolean;
  type?: 'normal' | 'delivered';
}

export interface CreateBatchesOptions extends SimpleBatchesOptions {
  documentSerial?: string;
  baseQuantity?: number;
  parallelQuantity?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SimpleBatchesDataService {

  /**
   * Transform order detail data into simplified batches dialog data
   */
  transformOrderDetailToBatchesData(
    orderDetail: OrderDetailInput,
    order: OrderInput,
    options: SimpleBatchesOptions = {}
  ): SimpleBatchesDialogData {
    
    const batchesKey = options.type === 'delivered' ? 'deliveredBatches' : 'batches';
    const rawBatches = orderDetail[batchesKey] || [];

    const transformedBatches: SimpleBatchData[] = rawBatches.map((batch: BatchInput) => ({
      bacth: batch.bacth || batch.batch || '',
      quantity: batch.quantity || 0,
      parallelQuantity: batch.parallelQuantity || 0,
      labels: this.transformLabels(batch.labels || []),
      expanded: false
    }));

    return {
      title: options.title || 'Detalle de Lotes',
      materialCode: orderDetail.material?.code,
      materialName: orderDetail.material?.name,
      batches: transformedBatches,
      showUMP: options.showUMP ?? true,
      showLabels: options.showLabels ?? true,
      documentSerial: order?.serial,
      baseQuantity: orderDetail.pararellQuantity || order?.pararellQuantity,
      parallelQuantity: orderDetail.baseQuantity || order?.baseQuantity
    };
  }

  /**
   * Transform work progress material item to batches data
   */
  transformWorkProgressMaterialToBatchesData(
    material: WorkProgressMaterialItem,
    options: SimpleBatchesOptions = {}
  ): SimpleBatchesDialogData {
    
    const transformedBatches: SimpleBatchData[] = material.batches.map((batch) => ({
      bacth: batch.batchNumber || '',
      quantity: batch.quantity || 0,
      parallelQuantity: batch.parallelQuantity || 0,
      labels: this.transformLabels(batch.labels || []),
      expanded: false
    }));

    return {
      title: options.title || `Lotes - ${material.materialCode}`,
      materialCode: material.materialCode,
      materialName: material.materialName,
      batches: transformedBatches,
      showUMP: options.showUMP ?? true,
      showLabels: options.showLabels ?? true,
      documentSerial: material.originalDocument?.serial,
      baseQuantity: material.baseQuantity,
      parallelQuantity: material.parallelQuantity
    };
  }

  private transformLabels(labels: LabelInput[]): SimpleLabelData[] {
    return labels.map((label: LabelInput) => ({
      label: label.label || '',
      quantity: label.quantity || 0,
      parallelQuantity: label.parallelQuantity || 0
    }));
  }

  /**
   * Create batches data from raw batch array (for simple cases)
   */
  createSimpleBatchesData(
    batches: BatchInput[],
    materialInfo?: MaterialInfo,
    options: CreateBatchesOptions = {}
  ): SimpleBatchesDialogData {
    
    const transformedBatches: SimpleBatchData[] = batches.map((batch: BatchInput) => ({
      bacth: batch.bacth || batch.batch || '',
      quantity: batch.quantity || 0,
      parallelQuantity: batch.parallelQuantity || 0,
      labels: this.transformLabels(batch.labels || []),
      expanded: false
    }));

    return {
      title: options.title || 'Detalle de Lotes',
      materialCode: materialInfo?.code,
      materialName: materialInfo?.name,
      batches: transformedBatches,
      showUMP: options.showUMP ?? true,
      showLabels: options.showLabels ?? true,
      documentSerial: options.documentSerial,
      baseQuantity: options.baseQuantity,
      parallelQuantity: options.parallelQuantity
    };
  }
}
